import React, { useContext, useEffect, useMemo, useState } from "react";

import { SurveyQuestion } from "../../../components/survey/SurveyQuestion";
import { ZeosSpinner } from "../../../components/Shared/ZeosSpinner";
import { ZeosApiContext } from "../../../context/ZeosApiContext";
import { IZeosUser } from "../../../services/auth/IZeosUser";
import { createLogger } from "../../../services/Logger";
import {
  buildSurveyUpdatePayload,
  hydrateSurveyAnswers,
} from "../../../services/survey/SurveyAnswerHelpers";
import {
  buildSurveySummarySections,
  SurveySummarySection,
} from "../../../services/survey/OnboardingSurveySummaryHelpers";
import {
  isQuestionType,
  isQuestionVisible,
  isQuestionnaireVisible,
  pruneHiddenAnswers,
} from "../../../services/survey/SurveyVisibilityHelpers";
import {
  ISurveyContract,
  ISurveyQuestionnaire,
} from "../../../services/zeos-api/contracts/Survey";
import { QuestionTypeEnum, SurveyCompletionMode } from "../../../services/zeos-api/enums/Survey";

const log = createLogger("OnboardingComprehensiveSurvey");

interface IProps {
  templateId: number;
  domainTitle: string;
  summaryTitle: string;
  summaryBody: string;
  zeosUser?: IZeosUser;
  initialAnswers?: Record<number, any>;
  onBackToCompletion: () => void;
  onFinished: () => void;
}

const OnboardingComprehensiveChrome: React.FC<{
  stepLabels: string[];
  activeStepIndex: number;
}> = ({ stepLabels, activeStepIndex }) => (
  <div className="mt-4">
    {stepLabels.length > 0 && (
      <div className="flex flex-wrap items-center gap-6 text-sm text-gray-600">
        {stepLabels.map((label, index) => {
          const isActive = index === activeStepIndex;
          const isLast = index === stepLabels.length - 1;
          return (
            <div key={`${label}-${index}`} className="flex items-center gap-2">
              <span
                className={
                  isActive
                    ? "text-zeos-black font-semibold border-b-2 border-zeos-orange-dk pb-1"
                    : "text-gray-700 pb-1"
                }
              >
                {label}
              </span>
              {!isLast && <div className="w-16 h-px bg-gray-300" />}
            </div>
          );
        })}
      </div>
    )}
  </div>
);

export const OnboardingComprehensiveSurvey: React.FC<IProps> = ({
  templateId,
  domainTitle,
  summaryTitle,
  summaryBody,
  zeosUser,
  initialAnswers,
  onBackToCompletion,
  onFinished,
}) => {
  const api = useContext(ZeosApiContext);

  const [template, setTemplate] = useState<ISurveyContract | null>(null);
  const [answers, setAnswers] = useState<Record<number, any>>(initialAnswers ?? {});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    setAnswers(initialAnswers ?? {});
  }, [initialAnswers]);

  useEffect(() => {
    let cancelled = false;

    const loadAsync = async () => {
      setLoading(true);
      setLoadError(null);
      setFormError(null);
      setSaveMessage(null);
      setTemplate(null);
      setStepIndex(0);

      try {
        const templateResponse = await api.Survey.getSurveyTemplate(templateId);
        if (cancelled) {
          return;
        }
        if (!templateResponse?.data) {
          setLoadError(templateResponse?.message || "Survey template not found.");
          return;
        }
        setTemplate(templateResponse.data);

        if (!zeosUser?.zeosUserId) {
          setAnswers(initialAnswers ?? {});
          return;
        }

        const surveyResponse = await api.Survey.getUserSurvey(
          templateResponse.data.surveyTemplateId,
          zeosUser.zeosUserId
        );
        if (cancelled) {
          return;
        }
        const latest = surveyResponse?.data;
        if (!latest?.answers?.length) {
          setAnswers(initialAnswers ?? {});
          return;
        }
        const hydrated = hydrateSurveyAnswers(
          templateResponse.data,
          latest.answers
        );
        setAnswers(hydrated);
      } catch (err) {
        if (!cancelled) {
          setLoadError("Unable to load survey.");
          log.error("Onboarding comprehensive survey load failed", err);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadAsync();
    return () => {
      cancelled = true;
    };
  }, [api.Survey, initialAnswers, templateId, zeosUser?.zeosUserId]);

  useEffect(() => {
    if (!template) {
      return;
    }
    setAnswers((prev) => {
      const pruned = pruneHiddenAnswers(template, prev);
      return pruned === prev ? prev : pruned;
    });
  }, [answers, template]);

  const contentSteps = useMemo(() => {
    if (!template) {
      return [] as ISurveyQuestionnaire[];
    }
    return template.questionnaires
      .slice()
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .filter(
        (qn) =>
          (qn.questions?.length ?? 0) > 0 ||
          (qn.displayName || "").toLowerCase().includes("summary")
      );
  }, [template]);

  const steps = useMemo(() => {
    const withQuestions = contentSteps.filter(
      (qn) =>
        (qn.questions?.length ?? 0) > 0 &&
        isQuestionnaireVisible(qn.visibilityRules, answers)
    );
    return [
      ...withQuestions,
      {
        questionnaireId: -1,
        displayName: "Summary",
        sortOrder: 999,
        questions: [],
      } as ISurveyQuestionnaire,
    ];
  }, [answers, contentSteps]);

  useEffect(() => {
    setSaveMessage(null);
    setFormError(null);
  }, [stepIndex]);

  useEffect(() => {
    setStepIndex((prev) => Math.min(prev, Math.max(0, steps.length - 1)));
  }, [steps.length]);

  const activeStep = steps[stepIndex];
  const isSummaryStep =
    !activeStep ||
    activeStep.questionnaireId < 0 ||
    (((activeStep.displayName || "").toLowerCase().includes("summary") &&
      (activeStep.questions?.length ?? 0) === 0));

  const visibleQuestions = useMemo(() => {
    if (!activeStep || !template || isSummaryStep) {
      return [];
    }
    if (!isQuestionnaireVisible(activeStep.visibilityRules, answers)) {
      return [];
    }
    return activeStep.questions
      .slice()
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .filter((question) => isQuestionVisible(question, answers));
  }, [activeStep, answers, isSummaryStep, template]);

  const summarySections = useMemo<SurveySummarySection[]>(() => {
    if (!template) {
      return [];
    }
    return buildSurveySummarySections(template, answers);
  }, [answers, template]);

  const validateCurrentStep = (): boolean => {
    if (isSummaryStep || !activeStep) {
      return true;
    }

    for (const question of visibleQuestions) {
      const value = answers[question.questionId];
      if (isQuestionType(question.questionTypeId, QuestionTypeEnum.FreeText)) {
        continue;
      }
      const minSelect = question.minSelect ?? 1;
      if (Array.isArray(value)) {
        if (value.length < minSelect) {
          setFormError(`Please answer: ${question.displayName}`);
          return false;
        }
      } else if (value == null || value === "") {
        setFormError(`Please answer: ${question.displayName}`);
        return false;
      }
    }

    setFormError(null);
    return true;
  };

  const saveSurveyAsync = async (): Promise<boolean> => {
    if (!template || !zeosUser?.zeosUserId) {
      setFormError("User not found.");
      return false;
    }

    setSaving(true);
    setSaveMessage(null);
    try {
      const pruned = pruneHiddenAnswers(template, answers);
      const payload = buildSurveyUpdatePayload(
        template,
        zeosUser.zeosUserId,
        pruned,
        { completionMode: SurveyCompletionMode.Comprehensive }
      );
      const updateResponse = await api.Survey.updateSurveyInstance(payload);
      const updateStatus = (updateResponse?.zeosStatus || "").toLowerCase();
      if (updateStatus === "error" || updateResponse?.data === false) {
        await api.Survey.submitSurveyInstance(payload);
      }
      setSaveMessage("Progress saved.");
      return true;
    } catch (err) {
      log.error("Onboarding comprehensive survey save failed", err);
      setFormError("Unable to save survey responses.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const goNext = async () => {
    if (!validateCurrentStep()) {
      return;
    }
    const saved = await saveSurveyAsync();
    if (!saved) {
      return;
    }
    if (isSummaryStep) {
      onFinished();
      return;
    }
    if (stepIndex >= steps.length - 1) {
      return;
    }
    setStepIndex((prev) => Math.min(prev + 1, steps.length - 1));
  };

  const goBack = () => {
    if (stepIndex === 0) {
      onBackToCompletion();
      return;
    }
    setStepIndex((prev) => Math.max(prev - 1, 0));
  };

  if (loading) {
    return <ZeosSpinner />;
  }

  if (loadError || !template) {
    return <div className="text-red-600">{loadError || "Survey template not found."}</div>;
  }

  return (
    <div className="space-y-6">
      <div className="bg-white border rounded p-6 space-y-6">
        <div>
          <h2 className="text-2xl font-semibold text-zeos-black">
            Comprehensive {domainTitle} Survey
          </h2>
          <p className="text-sm text-gray-600 mt-2">
            Continue through the full survey.
          </p>
        </div>

        <OnboardingComprehensiveChrome
          stepLabels={steps.map((step) => step.displayName || "Step")}
          activeStepIndex={stepIndex}
        />
      </div>

      <div className="bg-white border rounded p-6 space-y-6">
        {isSummaryStep ? (
          <>
            <div>
              <h2 className="text-2xl font-semibold text-zeos-black">
                {summaryTitle}
              </h2>
              <p className="text-sm text-gray-600 mt-2">{summaryBody}</p>
            </div>
            <div className="space-y-6">
              {summarySections.map((section) => (
                <div key={section.title} className="space-y-3">
                  <div className="text-sm font-semibold text-gray-700">
                    {section.title}
                  </div>
                  <div className="space-y-3">
                    {section.items.map((item) => (
                      <div
                        key={item.question.displayName}
                        className="border-l-4 border-zeos-orange-md pl-4 py-2"
                      >
                        <div className="text-sm font-semibold text-zeos-black">
                          {item.question.displayName}
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          {item.answerLabel}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-4 pt-2">
              <button
                type="button"
                className="px-6 py-2 rounded bg-zeos-orange-dk text-white text-sm disabled:opacity-50"
                onClick={() => {
                  void goNext();
                }}
                disabled={saving}
              >
                Continue Onboarding
              </button>
              <button
                type="button"
                className="ml-auto px-4 py-2 rounded border border-zeos-orange-dk text-zeos-orange-dk text-sm disabled:opacity-50"
                onClick={goBack}
                disabled={saving}
              >
                Back
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="text-2xl font-semibold text-zeos-black">
              {activeStep?.displayName}
            </h2>
            <div className="space-y-6">
              {visibleQuestions.map((question) => {
                const selectHint =
                  (isQuestionType(
                    question.questionTypeId,
                    QuestionTypeEnum.MultiChoice
                  ) ||
                    isQuestionType(
                      question.questionTypeId,
                      QuestionTypeEnum.RankedChoice
                    )) &&
                  question.maxSelect != null
                    ? isQuestionType(
                        question.questionTypeId,
                        QuestionTypeEnum.RankedChoice
                      )
                      ? `Select and rank up to ${question.maxSelect}.`
                      : `Select up to ${question.maxSelect}.`
                    : null;

                return (
                  <div
                    key={question.questionId}
                    className="border-l-4 border-zeos-orange-md pl-4 py-2"
                  >
                    <div className="text-sm font-semibold text-zeos-black">
                      {question.displayName}
                    </div>
                    {selectHint && (
                      <p className="text-xs text-gray-500 mt-1">{selectHint}</p>
                    )}
                    <div className="mt-3">
                      <SurveyQuestion
                        question={question}
                        isGate={false}
                        answers={answers}
                        setAnswers={setAnswers}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex items-center gap-4 pt-2">
              <button
                type="button"
                className="text-sm text-zeos-orange-dk hover:underline disabled:opacity-50"
                onClick={() => {
                  void saveSurveyAsync();
                }}
                disabled={saving}
              >
                Save
              </button>
              <button
                type="button"
                className="px-6 py-2 rounded bg-zeos-orange-dk text-white text-sm disabled:opacity-50"
                onClick={() => {
                  void goNext();
                }}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save & Continue"}
              </button>
              <button
                type="button"
                className="ml-auto px-4 py-2 rounded border border-zeos-orange-dk text-zeos-orange-dk text-sm disabled:opacity-50"
                onClick={goBack}
                disabled={saving}
              >
                Back
              </button>
            </div>
          </>
        )}

        {saveMessage && (
          <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded p-2">
            {saveMessage}
          </div>
        )}
        {formError && (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
            {formError}
          </div>
        )}
      </div>
    </div>
  );
};
